import React, { createContext, useContext, useEffect, useState } from 'react';
import { bridgeApi } from '@/lib/bridgeApi';

type EmployeeRole = 'super_admin' | 'admin' | 'moderator' | 'data_entry' | 'support' | 'staff';

interface Employee {
  id: string;
  name: string;
  email: string;
  phone: string | null;
  role: EmployeeRole;
  is_active: boolean;
  session_token?: string;
}

interface AuthContextType {
  user: Employee | null;
  session: any | null;
  employee: Employee | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signUp: (email: string, password: string, name: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  isAdmin: boolean;
  isSuperAdmin: boolean;
  isModerator: boolean;
  isDataEntry: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const STORAGE_KEY = 'patakare_employee_auth';

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [employee, setEmployee] = useState<Employee | null>(null);
  const [loading, setLoading] = useState(true);

  // Load stored session on mount
  useEffect(() => {
    const loadStoredSession = async () => {
      try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) {
          const employeeData = JSON.parse(stored) as Employee;
          // Verify session is still valid
          const result = await bridgeApi.call<{ valid: boolean; employee?: Employee }>('verify_employee_session', {
            session_token: employeeData.session_token
          });
          
          if (result.valid && result.employee) {
            setEmployee({ ...result.employee, session_token: employeeData.session_token });
          } else {
            localStorage.removeItem(STORAGE_KEY);
          }
        }
      } catch (error) {
        console.error('Failed to verify stored session:', error);
        localStorage.removeItem(STORAGE_KEY);
      } finally {
        setLoading(false);
      }
    };

    loadStoredSession();
  }, []);

  const signIn = async (email: string, password: string) => {
    try {
      const result = await bridgeApi.post<{ employee: Employee; session_token: string }>('employee_login', {
        email,
        password
      });

      const employeeData = { ...result.employee, session_token: result.session_token };
      setEmployee(employeeData);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(employeeData));
      return { error: null };
    } catch (error) {
      return { error: error as Error };
    }
  };

  const signUp = async (email: string, password: string, name: string) => {
    try {
      await bridgeApi.post('employee_register', {
        email,
        password,
        name
      });
      return { error: null };
    } catch (error) {
      return { error: error as Error };
    }
  };

  const signOut = async () => {
    if (employee?.session_token) {
      try {
        await bridgeApi.post('employee_logout', { session_token: employee.session_token });
      } catch (error) {
        console.error('Logout error:', error);
      }
    }
    setEmployee(null);
    localStorage.removeItem(STORAGE_KEY);
  };

  const isSuperAdmin = employee?.role === 'super_admin';
  const isAdmin = employee?.role === 'admin' || isSuperAdmin;
  const isModerator = employee?.role === 'moderator' || isAdmin;
  const isDataEntry = employee?.role === 'data_entry' || isModerator;

  return (
    <AuthContext.Provider value={{
      user: employee,
      session: employee ? { user: employee } : null,
      employee,
      loading,
      signIn,
      signUp,
      signOut,
      isAdmin,
      isSuperAdmin,
      isModerator,
      isDataEntry
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
